import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import {
  Lightning, TrendUp, TrendDown, ArrowUp, ArrowDown, Fire, Globe, CurrencyBtc, ArrowsClockwise, YoutubeLogo, Play, ArrowSquareOut, Spinner, Cube
} from "@phosphor-icons/react";

function fmt(val) {
  if (!val || val === 0) return "$0";
  if (val >= 1e12) return `$${(val / 1e12).toFixed(2)}T`;
  if (val >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
  if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${val.toFixed(2)}`;
}

function fmtPrice(val) {
  if (!val || val === 0) return "$0";
  if (val >= 1000) return `$${val.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  if (val >= 1) return `$${val.toFixed(2)}`;
  if (val >= 0.001) return `$${val.toFixed(4)}`;
  if (val >= 0.000001) return `$${val.toFixed(6)}`;
  return `$${val.toExponential(2)}`;
}

function fmtVol(val) {
  if (!val) return "$0";
  if (val >= 1e9) return `$${(val / 1e9).toFixed(1)}B`;
  if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
}

const CHAIN_TABS = [
  { id: "ethereum", label: "ETH", color: "#627EEA" },
  { id: "solana", label: "SOL", color: "#14F195" },
  { id: "bsc", label: "BNB", color: "#F0B90B" },
  { id: "pulsechain", label: "PLS", color: "#00FF00" },
  { id: "base", label: "BASE", color: "#0052FF" },
];

function ChainTrending() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeChain, setActiveChain] = useState("ethereum");

  useEffect(() => {
    axios.get(`${API}/trending-by-chain`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const chainData = data?.chains?.[activeChain];
  const tokens = chainData?.tokens || [];

  return (
    <div data-testid="chain-trending">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Cube size={11} weight="fill" className="text-amber-400/60" />
          <span className="text-[9px] uppercase tracking-wider text-zinc-600 font-medium">Top Trending by Chain</span>
          {loading && <Spinner size={10} className="animate-spin text-zinc-600 ml-1" />}
        </div>
      </div>

      {/* Chain tabs */}
      <div className="flex gap-1 mb-2.5" data-testid="chain-tabs">
        {CHAIN_TABS.map(c => (
          <button
            key={c.id}
            onClick={() => setActiveChain(c.id)}
            data-testid={`chain-tab-${c.id}`}
            className={`text-[10px] font-semibold px-3 py-1.5 rounded-sm border transition-all ${
              activeChain === c.id
                ? "border-amber-500/40 text-zinc-100"
                : "border-zinc-800/60 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700"
            }`}
            style={activeChain === c.id ? { backgroundColor: `${c.color}15`, borderColor: `${c.color}40` } : {}}
          >
            <span style={activeChain === c.id ? { color: c.color } : {}}>{c.label}</span>
          </button>
        ))}
      </div>

      {/* Token list */}
      {tokens.length > 0 ? (
        <div className="bg-zinc-900/30 border border-zinc-800/50 rounded-sm overflow-hidden" data-testid="chain-token-list">
          {/* Header */}
          <div className="grid grid-cols-12 gap-2 px-3 py-1.5 text-[8px] uppercase tracking-wider text-zinc-600 font-medium border-b border-zinc-800/40">
            <span className="col-span-1">#</span>
            <span className="col-span-3">Token</span>
            <span className="col-span-2 text-right">Price</span>
            <span className="col-span-2 text-right">24h</span>
            <span className="col-span-2 text-right">Volume</span>
            <span className="col-span-2 text-right">Liquidity</span>
          </div>
          {tokens.map((t, i) => {
            const isUp = t.change_24h >= 0;
            return (
              <a
                key={t.address + i}
                href={t.pair_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`chain-token-${i}`}
                className="grid grid-cols-12 gap-2 px-3 py-2 items-center hover:bg-zinc-800/30 transition-colors border-b border-zinc-800/20 last:border-0 group"
              >
                <span className="col-span-1 text-[10px] text-zinc-600 font-data">{i + 1}</span>
                <div className="col-span-3 flex items-center gap-2 min-w-0">
                  {t.image ? (
                    <img src={t.image} alt="" className="w-5 h-5 rounded-full shrink-0" loading="lazy" onError={e => e.target.style.display = "none"} />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-zinc-800 shrink-0 flex items-center justify-center">
                      <span className="text-[7px] text-zinc-500">{t.symbol?.[0]}</span>
                    </div>
                  )}
                  <div className="min-w-0">
                    <span className="text-[11px] font-medium text-zinc-200 block truncate group-hover:text-amber-300 transition-colors">{t.symbol}</span>
                    <span className="text-[9px] text-zinc-600 block truncate">{t.name}</span>
                  </div>
                </div>
                <span className="col-span-2 text-[11px] font-data text-zinc-300 text-right">{fmtPrice(t.price_usd)}</span>
                <span className={`col-span-2 text-[11px] font-data text-right flex items-center justify-end gap-0.5 ${isUp ? "text-emerald-400" : "text-red-400"}`}>
                  {isUp ? <ArrowUp size={8} weight="bold" /> : <ArrowDown size={8} weight="bold" />}
                  {isUp ? "+" : ""}{t.change_24h}%
                </span>
                <span className="col-span-2 text-[10px] font-data text-zinc-500 text-right">{fmtVol(t.volume_24h)}</span>
                <span className="col-span-2 text-[10px] font-data text-zinc-500 text-right">{fmtVol(t.liquidity)}</span>
              </a>
            );
          })}
        </div>
      ) : !loading ? (
        <div className="text-center py-6 text-zinc-600 text-xs">No trending tokens found for this chain</div>
      ) : null}
    </div>
  );
}

function ClearValueVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/youtube-clearvalue`)
      .then(r => setVideos(r.data.videos || []))
      .catch(() => setVideos([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="clearvalue-videos">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <YoutubeLogo size={13} weight="fill" className="text-red-500" />
          <span className="text-[9px] uppercase tracking-wider text-zinc-600 font-medium">ClearValue Tax — Latest Videos</span>
          {loading && <Spinner size={10} className="animate-spin text-zinc-600 ml-1" />}
        </div>
        <a
          href="https://youtube.com/@clearvaluetax9382"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="clearvalue-channel-link"
          className="flex items-center gap-1 text-[9px] text-zinc-600 hover:text-red-400 transition-colors"
        >
          Subscribe <ArrowSquareOut size={9} />
        </a>
      </div>
      {videos.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
          {videos.slice(0, 8).map((v, i) => (
            <a
              key={v.id}
              href={v.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`clearvalue-video-${i}`}
              className="group block bg-zinc-900/60 border border-zinc-800/50 rounded-sm overflow-hidden hover:border-red-900/40 transition-colors"
            >
              <div className="relative">
                <img
                  src={v.thumbnail}
                  alt={v.title}
                  className="w-full h-[100px] object-cover group-hover:opacity-90 transition-opacity"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                {v.length && (
                  <span className="absolute bottom-1 right-1 bg-black/80 text-[9px] text-zinc-300 font-data px-1 py-0.5 rounded-sm">{v.length}</span>
                )}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Play size={28} weight="fill" className="text-white/90 drop-shadow-lg" />
                </div>
              </div>
              <div className="px-2.5 py-2">
                <p className="text-[11px] font-medium text-zinc-300 leading-snug line-clamp-2 group-hover:text-red-400 transition-colors">{v.title}</p>
                <div className="flex items-center gap-2 mt-1 text-[9px] text-zinc-600">
                  <span>{v.published}</span>
                  {v.views && <span>{v.views}</span>}
                </div>
              </div>
            </a>
          ))}
        </div>
      ) : (
        !loading && (
          <a
            href="https://youtube.com/@clearvaluetax9382"
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-zinc-900/40 border border-zinc-800/40 rounded-sm p-4 text-center hover:border-red-900/30 transition-colors"
          >
            <YoutubeLogo size={24} weight="fill" className="text-red-500 mx-auto mb-2" />
            <p className="text-xs text-zinc-400">Visit ClearValue Tax on YouTube</p>
            <p className="text-[10px] text-zinc-600 mt-0.5">Economic news, market analysis & investing</p>
          </a>
        )
      )}
    </div>
  );
}

function TokenAlert({ token, index }) {
  const navigate = useNavigate();
  const isSpike = token.source === "volume_spike";
  const isUp = (token.price_change_24h || 0) >= 0;
  const spikeVal = token.volume_spike_pct || 0;
  const hasBigSpike = Math.abs(spikeVal) > 30;

  const dexUrl = `https://dexscreener.com/search?q=${encodeURIComponent(token.symbol || token.name || "")}`;

  const handleClick = () => {
    if (token.id) {
      navigate(`/asset/crypto/${encodeURIComponent(token.id)}`);
    }
  };

  return (
    <div
      data-testid={`trending-token-${index}`}
      className={`relative flex items-center gap-3 px-4 py-3 rounded-sm border transition-all duration-300 text-left group overflow-hidden ${
        isSpike
          ? "bg-amber-950/15 border-amber-800/40 hover:border-amber-600/60 hover:bg-amber-950/25"
          : "bg-zinc-900/40 border-zinc-800/50 hover:border-zinc-600/60 hover:bg-zinc-900/60"
      }`}
      style={{ animationDelay: `${index * 80}ms` }}
    >
      {/* Pulse indicator for spikes */}
      {isSpike && hasBigSpike && (
        <span className="absolute top-2 right-2 flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
        </span>
      )}

      {/* Clickable body — navigates to asset detail */}
      <button
        onClick={handleClick}
        data-testid={`trending-token-body-${index}`}
        className="flex items-center gap-3 flex-1 min-w-0 text-left"
      >
        {/* Icon */}
        <div className={`w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 ${
          isSpike ? "bg-amber-900/30" : "bg-zinc-800/60"
        }`}>
          {token.thumb ? (
            <img src={token.thumb} alt={token.symbol} className="w-5 h-5 rounded-full" loading="lazy" />
          ) : (
            <CurrencyBtc size={16} className={isSpike ? "text-amber-400" : "text-zinc-400"} />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-zinc-100 truncate">{token.symbol}</span>
            <Badge className={`text-[7px] px-1.5 py-0 border-0 rounded-sm h-3.5 ${
              isSpike ? "bg-amber-900/50 text-amber-400" : "bg-violet-900/30 text-violet-400"
            }`}>
              {isSpike ? "VOLUME SPIKE" : "TRENDING"}
            </Badge>
          </div>
          <span className="text-[10px] text-zinc-500 truncate block">{token.name}</span>
        </div>

        {/* Metrics */}
        <div className="text-right flex-shrink-0">
          {token.current_price > 0 && (
            <p className="text-[11px] font-data font-medium text-zinc-200">{fmt(token.current_price)}</p>
          )}
          <div className="flex items-center justify-end gap-1">
            {isSpike && spikeVal !== 0 && (
              <span className={`text-[10px] font-data flex items-center gap-0.5 ${
                spikeVal > 0 ? "text-emerald-400" : "text-red-400"
              }`}>
                {spikeVal > 0 ? <ArrowUp size={8} weight="bold" /> : <ArrowDown size={8} weight="bold" />}
                {spikeVal > 0 ? "+" : ""}{spikeVal.toFixed(0)}% vol
              </span>
            )}
            {!isSpike && token.price_change_24h !== 0 && (
              <span className={`text-[10px] font-data flex items-center gap-0.5 ${
                isUp ? "text-emerald-400" : "text-red-400"
              }`}>
                {isUp ? <ArrowUp size={8} weight="bold" /> : <ArrowDown size={8} weight="bold" />}
                {isUp ? "+" : ""}{token.price_change_24h?.toFixed(1)}%
              </span>
            )}
          </div>
        </div>
      </button>

      {/* DexScreener external link */}
      <a
        href={dexUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        data-testid={`trending-token-dex-${index}`}
        title="View on DexScreener"
        className="flex items-center gap-1 text-[9px] text-zinc-500 hover:text-emerald-400 transition-colors px-2 py-1 rounded-sm border border-zinc-800 hover:border-emerald-700/60 shrink-0"
      >
        <span className="font-semibold">DEX</span>
        <ArrowSquareOut size={10} weight="bold" />
      </a>
    </div>
  );
}

function ChainAlert({ chain, index }) {
  const isUp = chain.avg_change_pct >= 0;

  return (
    <div
      data-testid={`trending-chain-${index}`}
      className={`relative flex items-center gap-3 px-4 py-3 rounded-sm border transition-all duration-300 overflow-hidden ${
        isUp
          ? "bg-emerald-950/10 border-emerald-800/30 hover:border-emerald-600/40"
          : "bg-red-950/10 border-red-800/30 hover:border-red-600/40"
      }`}
      style={{ animationDelay: `${index * 80}ms` }}
    >
      {/* Pulse for high spikes */}
      {Math.abs(chain.avg_change_pct) > 40 && (
        <span className="absolute top-2 right-2 flex h-2 w-2">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${isUp ? "bg-emerald-400" : "bg-red-400"}`} />
          <span className={`relative inline-flex rounded-full h-2 w-2 ${isUp ? "bg-emerald-500" : "bg-red-500"}`} />
        </span>
      )}

      <div className={`w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 ${
        isUp ? "bg-emerald-900/30" : "bg-red-900/30"
      }`}>
        <Globe size={16} className={isUp ? "text-emerald-400" : "text-red-400"} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-zinc-100">{chain.name}</span>
          <Badge className={`text-[7px] px-1.5 py-0 border-0 rounded-sm h-3.5 ${
            isUp ? "bg-emerald-900/50 text-emerald-400" : "bg-red-900/50 text-red-400"
          }`}>
            {isUp ? "VOLUME SURGE" : "VOLUME DROP"}
          </Badge>
        </div>
        <span className="text-[10px] text-zinc-500">{chain.protocol_count} protocols tracked</span>
      </div>

      <div className="text-right flex-shrink-0">
        <p className="text-[11px] font-data font-medium text-zinc-200">{fmt(chain.volume_24h)}</p>
        <span className={`text-[10px] font-data flex items-center justify-end gap-0.5 ${
          isUp ? "text-emerald-400" : "text-red-400"
        }`}>
          {isUp ? <TrendUp size={10} weight="bold" /> : <TrendDown size={10} weight="bold" />}
          {isUp ? "+" : ""}{chain.avg_change_pct}% avg
        </span>
      </div>
    </div>
  );
}

export default function TrendingAlerts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/trending-alerts`);
      setData(resp.data);
    } catch (e) {
      console.error("Failed to fetch trending alerts:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const tokens = data?.trending_tokens || [];
  const hasAlerts = tokens.length > 0;

  if (loading || !hasAlerts) return null;

  return (
    <section data-testid="trending-alerts" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Fire size={16} weight="fill" className="text-amber-400" />
            <span className="absolute -top-0.5 -right-0.5 flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-500" />
            </span>
          </div>
          <h2 className="text-[10px] uppercase tracking-[0.15em] text-amber-400/80 font-medium">Trending Alerts</h2>
          <span className="text-[9px] text-zinc-600">
            {tokens.length} active
          </span>
        </div>
        <button
          data-testid="toggle-alerts-btn"
          onClick={() => setCollapsed(!collapsed)}
          className="text-[9px] text-zinc-500 hover:text-zinc-300 transition-colors px-2 py-0.5 border border-zinc-800 rounded-sm"
        >
          {collapsed ? "Show" : "Hide"}
        </button>
      </div>

      {!collapsed && (
        <div className="space-y-3 animate-in fade-in duration-300">
          {/* Trending Tokens */}
          {tokens.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Lightning size={11} weight="fill" className="text-amber-400/60" />
                <span className="text-[9px] uppercase tracking-wider text-zinc-600 font-medium">Trending Tokens</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2" data-testid="trending-tokens-grid">
                {tokens.slice(0, 8).map((t, i) => (
                  <TokenAlert key={t.id || i} token={t} index={i} />
                ))}
              </div>
            </div>
          )}

          {/* ClearValue Tax Newsletter & Videos */}
          <ChainTrending />
          <ClearValueVideos />
        </div>
      )}
    </section>
  );
}
